import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect, useState } from 'react';
import { Sparkles, Loader2, Send } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Alert } from '../ui/Alert';
import { NativeSelect } from '../ui/NativeSelect';
import { DatePicker } from '../ui/DatePicker';
import { AspirationFormData, AspirationCategory } from '@/types/aspirations';
import { useAspirationsStore } from '@/store/aspirationsStore';
import { aiService } from '@/services/aiService';
import { personaService } from '@/services/personaService';

const aspirationSchema = z.object({
  title: z.string().min(1, 'Title is required').max(100, 'Title must be less than 100 characters'),
  description: z.string().min(10, 'Description must be at least 10 characters').max(500, 'Description must be less than 500 characters'),
  category: z.enum(['Career', 'Financial', 'Health', 'Education', 'Personal', 'Family', 'Travel', 'Social', 'Spiritual']),
  deadline: z.string().optional(),
});

type AspirationFormInputs = z.infer<typeof aspirationSchema>;

interface AspirationFormProps {
  isOpen: boolean;
  onClose: () => void;
  aspirationId?: string; // ID of aspiration to edit
  isEditing?: boolean;
}

const categories: AspirationCategory[] = [
  'Career', 'Financial', 'Health', 'Education', 'Personal', 
  'Family', 'Travel', 'Social', 'Spiritual'
];


export function AspirationForm({ 
  isOpen, 
  onClose, 
  aspirationId,
  isEditing = false 
}: AspirationFormProps) {
  const { createAspiration, updateAspiration, getAspirationById, aspirations } = useAspirationsStore();
  
  // Get the aspiration data if editing
  const existingAspiration = aspirationId ? getAspirationById(aspirationId) : null;

  // AI assistant state
  const [aiInput, setAiInput] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [showAiSection, setShowAiSection] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    getValues,
    watch
  } = useForm<AspirationFormInputs>({
    resolver: zodResolver(aspirationSchema),
    defaultValues: {
      title: '',
      description: '',
      category: 'Personal',
      deadline: '',
    }
  });

  const deadlineValue = watch('deadline');

  // Update form values when aspiration data changes
  useEffect(() => {
    if (existingAspiration && isOpen) {
      setValue('title', existingAspiration.title);
      setValue('description', existingAspiration.description);
      setValue('category', existingAspiration.category);
      setValue('deadline', existingAspiration.deadline ? 
        (() => {
          try {
            const date = new Date(existingAspiration.deadline);
            return isNaN(date.getTime()) ? '' : date.toISOString().split('T')[0];
          } catch {
            return '';
          }
        })() : '');
    } else if (!isEditing && isOpen) {
      // Reset to default values for new aspirations
      reset({
        title: '',
        description: '',
        category: 'Personal',
        deadline: '',
      });
    }
  }, [existingAspiration, isOpen, isEditing, setValue, reset]);

  const handleFormSubmit = async (data: AspirationFormInputs) => {
    try {
      if (isEditing && aspirationId) {
        // Update existing aspiration
        updateAspiration(aspirationId, {
          title: data.title,
          description: data.description,
          category: data.category,
          deadline: data.deadline ? new Date(data.deadline) : undefined,
        });
      } else {
        // Create new aspiration
        const formData: AspirationFormData = {
          ...data,
          priority: 1, // New aspirations always get top priority
          deadline: data.deadline ? new Date(data.deadline) : undefined,
        };
        createAspiration(formData);
      }
      
      handleClose();
    } catch (error) {
      console.error('Error saving aspiration:', error);
    }
  };

  const handleClose = () => {
    reset();
    setAiInput('');
    setAiError(null);
    setShowAiSection(false);
    onClose();
  };

  // AI handler function
  const handleAskAI = async () => {
    if (!aiInput.trim()) {
      setAiError('Please describe what you want to achieve');
      return;
    }

    if (!aiService.isConfigured()) {
      setAiError('AI service not configured. Please add your API key in Settings.');
      return;
    }

    setIsAiLoading(true);
    setAiError(null);
    
    try {
      const profile = personaService.getProfile();
      
      // Get current form values to provide as context
      const currentValues = getValues();
      
      const suggestion = await aiService.generateAspirationSuggestion({
        userInput: aiInput,
        existingAspirations: aspirations.map(a => ({
          title: a.title,
          category: a.category,
          description: a.description
        })),
        userProfile: {
          name: profile.name,
          jobRole: profile.jobRole,
          age: profile.age
        },
        currentFormData: {
          title: currentValues.title,
          description: currentValues.description,
          category: currentValues.category,
          deadline: currentValues.deadline
        }
      });
      
      // Populate form fields with AI suggestion
      setValue('title', suggestion.title);
      setValue('description', suggestion.description);
      setValue('category', suggestion.category);
      
      // Clear AI input after successful generation
      setAiInput('');
      setShowAiSection(false);
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'Failed to generate suggestion');
    } finally {
      setIsAiLoading(false);
    }
  };


  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      title={isEditing ? 'Edit Aspiration' : 'Create New Aspiration'}
      scrollBehavior="inside"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} className="stack-spacious">
        {/* Title */}
        <div className="stack-normal">
          <label className="text-label">Title *</label>
          <Input
            data-testid="aspiration-title-input"
            {...register('title')}
            placeholder="What do you want to achieve?"
            error={errors.title?.message}
          />
        </div>

        {/* Description */}
        <div className="stack-normal">
          <label className="text-label">Description *</label>
          <textarea
            {...register('description')}
            placeholder="Describe your aspiration in detail..."
            className="form-textarea"
          />
          {errors.description && (
            <p className="form-error-text mt-2">{errors.description.message}</p>
          )}
        </div>

        {/* Category */}
        <div className="stack-normal">
          <label className="text-label">Category *</label>
          <NativeSelect {...register('category')}>
            {categories.map(category => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </NativeSelect>
        </div>

        {/* Deadline */}
        <div className="stack-normal">
          <label className="text-label">Deadline</label>
          <DatePicker
            value={deadlineValue ? new Date(deadlineValue) : undefined}
            onChange={(date) => {
              setValue('deadline', date ? date.toISOString().split('T')[0] : '');
            }}
            minDate={new Date()}
            placeholder="Select deadline (optional)"
          />
        </div>

        {/* AI Assistant Section */}
        {!isEditing && aiService.isConfigured() && (
          <div className="stack-normal border-t pt-4">
            {!showAiSection ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAiSection(true)}
                className="w-full gap-2"
              >
                <Sparkles className="icon-md" />
                Ask AI for Help
              </Button>
            ) : (
              <div className="stack-relaxed">
                <div className="layout-row-between">
                  <label className="text-label layout-row-gap">
                    <Sparkles className="icon-md text-primary" />
                    Ask AI
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAiSection(false);
                      setAiInput('');
                      setAiError(null);
                    }}
                    className="text-muted-xs hover:text-foreground"
                  >
                    Close
                  </button>
                </div>
                
                <div className="flex gap-2">
                  <textarea
                    value={aiInput}
                    onChange={(e) => setAiInput(e.target.value)}
                    placeholder="Describe your aspiration or ask AI to enhance what you've already written... (e.g., 'I want to become healthier' or 'make this more specific')"
                    className="form-textarea flex-1 min-h-[80px]"
                    disabled={isAiLoading}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.ctrlKey) {
                        e.preventDefault();
                        handleAskAI();
                      }
                    }}
                  />
                </div>
                
                {aiError && (
                  <Alert variant="destructive" className="text-sm">
                    {aiError}
                  </Alert>
                )}
                
                <div className="layout-row-between">
                  <p className="text-muted-xs">
                    AI will enhance current form data and consider your {aspirations.length} existing aspiration{aspirations.length !== 1 ? 's' : ''}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleAskAI}
                    disabled={isAiLoading || !aiInput.trim()}
                    className="gap-2"
                  >
                    {isAiLoading ? (
                      <>
                        <Loader2 className="icon-sm animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Send className="icon-sm" />
                        Generate
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : isEditing ? 'Update Aspiration' : 'Create Aspiration'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}